import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import { 
  Car, 
  ArrowLeft,
  Calendar,
  MapPin,
  Clock,
  Users,
  Plane,
  LogOut,
  User,
  Loader2,
  BarChart3,
  BookOpen,
  UserPlus,
  Edit,
  Phone,
  Mail
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, logout, loading: authLoading, getToken } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    // Admin panel requires authentication and admin rights
    if (!authLoading && !user) {
      // Not logged in - redirect to login
      navigate('/login?redirect=/dashboard');
    } else if (!authLoading && user && !user.is_admin) {
      // Logged in but not admin - show access denied
      toast.error('Access Denied. Admin privileges required.');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    const token = getToken();
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    try {
      const [statsRes, bookingsRes, driversRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers }),
        fetch(`${API}/bookings`, { headers }),
        fetch(`${API}/drivers/signups`, { headers })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (bookingsRes.ok) setBookings(await bookingsRes.json());
      if (driversRes.ok) setDrivers(await driversRes.json());
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateBooking = async (bookingId, updates) => {
    const token = getToken();
    try {
      const response = await fetch(`${API}/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        toast.success('Booking updated');
        fetchData();
        setEditModalOpen(false);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const updateDriverStatus = async (driverId, status) => {
    const token = getToken();
    try {
      const response = await fetch(`${API}/drivers/signups/${driverId}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success('Driver status updated');
        fetchData();
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast.error('Failed to update driver status');
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'confirmed': return 'status-confirmed';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      case 'approved': return 'status-confirmed';
      case 'rejected': return 'status-cancelled';
      default: return 'status-pending';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
      </div>
    );
  }

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-white">NeoRide</span>
            </div>
            <span className="text-xs font-bold uppercase tracking-widest text-violet-400 ml-2 bg-violet-900/50 px-2 py-1 rounded-full">Admin Panel</span>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
              <span className="text-sm text-slate-300 hidden sm:block">{user.name}</span>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={logout}
              className="text-slate-400 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Stats Grid */}
          {stats && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Total Bookings</span>
                  <BookOpen className="w-4 h-4 text-slate-300" />
                </div>
                <p className="text-3xl font-medium text-slate-900">{stats.bookings.total}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Pending</span>
                  <Clock className="w-4 h-4 text-amber-500" />
                </div>
                <p className="text-3xl font-medium text-amber-600">{stats.bookings.pending}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Confirmed</span>
                  <BarChart3 className="w-4 h-4 text-emerald-500" />
                </div>
                <p className="text-3xl font-medium text-emerald-600">{stats.bookings.confirmed}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Driver Apps</span>
                  <UserPlus className="w-4 h-4 text-violet-500" />
                </div>
                <p className="text-3xl font-medium text-slate-900">{stats.drivers.pending}</p>
                <p className="text-xs text-slate-400">pending review</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="bookings" className="space-y-6">
            <TabsList className="bg-white border border-slate-200 shadow-sm">
              <TabsTrigger value="bookings" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white" data-testid="tab-bookings">
                Bookings
              </TabsTrigger>
              <TabsTrigger value="drivers" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white" data-testid="tab-drivers">
                Driver Applications
              </TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="space-y-4" data-testid="bookings-content">
              {bookings.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No bookings yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bookings.map((booking) => (
                    <div 
                      key={booking.booking_id}
                      className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
                      data-testid={`admin-booking-${booking.booking_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm text-slate-900">{booking.booking_id}</span>
                            {booking.is_airport_transfer && (
                              <span className="flex items-center gap-1 text-xs text-violet-600">
                                <Plane className="w-3 h-3" /> Airport
                              </span>
                            )}
                          </div>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>
                        <Dialog open={editModalOpen && selectedBooking?.booking_id === booking.booking_id} onOpenChange={(open) => {
                          setEditModalOpen(open);
                          if (open) setSelectedBooking(booking);
                        }}>
                          <DialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900" data-testid={`edit-booking-${booking.booking_id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-white border-slate-200">
                            <DialogHeader>
                              <DialogTitle className="text-slate-900">Edit Booking {booking.booking_id}</DialogTitle>
                            </DialogHeader>
                            <BookingEditForm 
                              booking={booking} 
                              onSave={(updates) => updateBooking(booking.booking_id, updates)} 
                            />
                          </DialogContent>
                        </Dialog>
                      </div>
                      
                      <div className="grid sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-slate-400 text-xs mb-1">Customer</p>
                          <p className="text-slate-900">{booking.full_name}</p>
                          <p className="text-slate-500 text-xs">{booking.contact_number}</p>
                          <p className="text-slate-500 text-xs">{booking.email}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs mb-1">Journey</p>
                          <p className="text-slate-600 text-xs flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-emerald-500" /> {booking.pickup_location}
                          </p>
                          <p className="text-slate-600 text-xs flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-red-500" /> {booking.dropoff_location}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs mb-1">Schedule</p>
                          <p className="text-slate-600 text-xs flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {booking.journey_date}
                          </p>
                          <p className="text-slate-600 text-xs flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {booking.pickup_time}
                          </p>
                          <p className="text-slate-600 text-xs flex items-center gap-1">
                            <Users className="w-3 h-3" /> {booking.passengers} passengers
                          </p>
                        </div>
                      </div>
                      
                      {booking.fare && (
                        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-slate-500 text-sm">Fare</span>
                          <span className="text-slate-900 font-medium">£{booking.fare}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="drivers" className="space-y-4" data-testid="drivers-content">
              {drivers.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200">
                  <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No driver applications yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {drivers.map((driver) => (
                    <div 
                      key={driver.driver_id}
                      className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
                      data-testid={`driver-${driver.driver_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono text-sm text-slate-900">{driver.driver_id}</span>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(driver.status)}`}>
                              {driver.status}
                            </span>
                          </div>
                          <p className="text-slate-900 font-medium">{driver.full_name}</p>
                          <p className="text-slate-500 text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {driver.contact_number}
                          </p>
                          <p className="text-slate-500 text-sm flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {driver.email}
                          </p>
                          {driver.licence_number && (
                            <p className="text-slate-500 text-sm mt-1">Licence: {driver.licence_number}</p>
                          )}
                        </div>
                        
                        {driver.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button 
                              size="sm"
                              onClick={() => updateDriverStatus(driver.driver_id, 'approved')}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`approve-${driver.driver_id}`}
                            >
                              Approve
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => updateDriverStatus(driver.driver_id, 'rejected')}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`reject-${driver.driver_id}`}
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

// Booking Edit Form Component
const BookingEditForm = ({ booking, onSave }) => {
  const [status, setStatus] = useState(booking.status);
  const [fare, setFare] = useState(booking.fare || '');
  const [driver, setDriver] = useState(booking.driver_assigned || '');
  const [vehicle, setVehicle] = useState(booking.vehicle_assigned || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      status,
      fare: fare ? parseFloat(fare) : null,
      driver_assigned: driver || null,
      vehicle_assigned: vehicle || null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-slate-700">Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label className="text-slate-700">Fare (£)</Label>
        <Input 
          type="number"
          step="0.01"
          value={fare}
          onChange={(e) => setFare(e.target.value)}
          placeholder="Enter fare amount"
          className="bg-slate-50 border-slate-200 text-slate-900"
          data-testid="input-fare"
        />
      </div>
      
      <div className="space-y-2">
        <Label className="text-slate-700">Assigned Driver</Label>
        <Input 
          value={driver}
          onChange={(e) => setDriver(e.target.value)}
          placeholder="Driver name"
          className="bg-slate-50 border-slate-200 text-slate-900"
          data-testid="input-driver"
        />
      </div>
      
      <div className="space-y-2">
        <Label className="text-slate-700">Vehicle</Label>
        <Input 
          value={vehicle}
          onChange={(e) => setVehicle(e.target.value)}
          placeholder="Vehicle details"
          className="bg-slate-50 border-slate-200 text-slate-900"
          data-testid="input-vehicle"
        />
      </div>
      
      <Button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700" data-testid="save-booking-btn">
        Save Changes
      </Button>
    </form>
  );
};

export default AdminDashboard;
